import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Search } from "lucide-react";
import { Button as TamaguiButton, XStack, YStack } from "tamagui";

import { supabase } from "@/integrations/supabase/client";
import { HELPER_PACKS, SUPPORT_CATEGORIES } from "@/lib/support-data";
import { fetchSavedSupportPackSlugs, toggleSavedSupportPack } from "@/lib/savedSupport";
import { useAuth } from "@/contexts/AuthContext";
import { getMemberCountryLabel } from "../../shared/location";
import {
  KINDRED_FEEDBACK_CHANNEL_PATH,
  getSupportCountryResourceBundle,
  getSupportSearchResults,
  hasLiveSupportForCountry,
  isSupportPlaybookAvailableForCountry,
  isSupportPausedCountry,
} from "../../shared/support";
import {
  AppButton,
  AppCard,
  AppCardBody,
  AppEmptyState,
  AppInput,
  AppLoadingState,
  AppSectionHeader,
  AppText,
  kindredFontFamilies,
} from "../../ui-foundation";

const GUIDED_SUPPORT_STARTS = [
  {
    slug: "benefits-check",
    emoji: "💰",
    title: "Money feels tight",
    description: "Start with the quickest check for missing help and first practical steps.",
  },
  {
    slug: "separation-divorce",
    emoji: "⚖️",
    title: "Separation feels messy",
    description: "Go straight to the first steps when everything feels tangled.",
  },
  {
    slug: "building-support-network",
    emoji: "💗",
    title: "I'm carrying too much alone",
    description: "Find steadier support and one gentler next step from here.",
  },
  {
    slug: "getting-ehcp",
    emoji: "📚",
    title: "School or additional needs help",
    description: "Get clearer on the right school support steps and what to ask for next.",
  },
] as const;

const Support = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [savedPackSlugs, setSavedPackSlugs] = useState<string[]>([]);
  const [memberCountryCode, setMemberCountryCode] = useState<string | null>(null);
  const [supportCountryLoading, setSupportCountryLoading] = useState(true);

  const { filteredCategories, filteredHelperPacks, hasActiveSearch } = useMemo(
    () => getSupportSearchResults(SUPPORT_CATEGORIES, HELPER_PACKS, search),
    [search],
  );

  useEffect(() => {
    if (!user?.id) {
      setSavedPackSlugs([]);
      return;
    }

    fetchSavedSupportPackSlugs(user.id)
      .then(setSavedPackSlugs)
      .catch((error) => {
        console.error("Could not load saved support packs:", error);
      });
  }, [user?.id]);

  useEffect(() => {
    let active = true;

    if (!user?.id) {
      setMemberCountryCode(null);
      setSupportCountryLoading(false);
      return () => {
        active = false;
      };
    }

    setSupportCountryLoading(true);
    void supabase
      .from("my_private_profile")
      .select("country_code")
      .maybeSingle()
      .then(({ data }) => {
        if (!active) {
          return;
        }

        setMemberCountryCode(data?.country_code ?? null);
        setSupportCountryLoading(false);
      })
      .catch(() => {
        if (!active) {
          return;
        }

        setMemberCountryCode(null);
        setSupportCountryLoading(false);
      });

    return () => {
      active = false;
    };
  }, [user?.id]);

  const savedHelperPacks = useMemo(
    () =>
      savedPackSlugs
        .map((slug) => HELPER_PACKS.find((pack) => pack.slug === slug) ?? null)
        .filter(
          (pack): pack is (typeof HELPER_PACKS)[number] =>
            Boolean(pack) && isSupportPlaybookAvailableForCountry(pack.slug, memberCountryCode),
        ),
    [savedPackSlugs, memberCountryCode],
  );
  const visibleHelperPacks = useMemo(
    () =>
      filteredHelperPacks.filter((pack) =>
        isSupportPlaybookAvailableForCountry(pack.slug, memberCountryCode),
      ),
    [filteredHelperPacks, memberCountryCode],
  );
  const liveSupportAvailable = hasLiveSupportForCountry(memberCountryCode);
  const supportPaused = isSupportPausedCountry(memberCountryCode);
  const supportCountryLabel = memberCountryCode ? getMemberCountryLabel(memberCountryCode) : "your country";
  const supportResourceBundle = getSupportCountryResourceBundle(memberCountryCode);

  const handleToggleSavedPack = async (packSlug: string) => {
    if (!user?.id) return;

    const isSaved = savedPackSlugs.includes(packSlug);
    const result = await toggleSavedSupportPack(packSlug, user.id, isSaved);

    if (result.error) {
      console.error("Could not update saved support pack:", result.error);
      return;
    }

    setSavedPackSlugs((current) =>
      isSaved ? current.filter((slug) => slug !== packSlug) : [packSlug, ...current.filter((slug) => slug !== packSlug)],
    );
  };

  return (
    <YStack gap="$md" pt="$xs" pb="$md">
      {supportCountryLoading ? (
        <AppLoadingState
          title="Loading support"
          body="Checking the right support guidance for your country."
        />
      ) : liveSupportAvailable ? (
        <>
          <YStack gap="$sm">
            <AppCard backgroundColor="$panelSoftBackground" borderColor="$borderStrong">
              <AppCardBody gap="$sm">
                <YStack gap={4}>
                  <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
                    What do you need today?
                  </AppText>
                  <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
                    You do not need to read everything. Pick the closest fit and we&apos;ll take you straight to a
                    practical starting point.
                  </AppText>
                </YStack>

                <YStack gap="$sm">
                  {GUIDED_SUPPORT_STARTS.map((action) => (
                    <TamaguiButton
                      key={action.slug}
                      unstyled
                      type="button"
                      onPress={() => navigate(`/support/pack/${action.slug}`)}
                      alignItems="stretch"
                      justifyContent="stretch"
                      pressStyle={{ opacity: 0.95 }}
                    >
                      <AppCard backgroundColor="$background" borderColor="$borderColor">
                        <AppCardBody gap="$xs">
                          <XStack alignItems="flex-start" gap="$sm">
                            <AppText fontSize={24} lineHeight={26}>
                              {action.emoji}
                            </AppText>
                            <YStack flex={1} gap={4}>
                              <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
                                {action.title}
                              </AppText>
                              <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
                                {action.description}
                              </AppText>
                            </YStack>
                          </XStack>
                        </AppCardBody>
                      </AppCard>
                    </TamaguiButton>
                  ))}
                </YStack>
              </AppCardBody>
            </AppCard>

            {savedHelperPacks.length ? (
              <YStack gap="$sm">
                <AppSectionHeader
                  title="Saved for later"
                  subtitle="The helper packs you wanted to keep close."
                />
                <YStack gap="$sm">
                  {savedHelperPacks.map((pack) => (
                    <AppCard key={pack.slug}>
                      <AppCardBody gap="$sm">
                        <XStack alignItems="flex-start" gap="$sm">
                          <AppText fontSize={24} lineHeight={26}>
                            {pack.emoji}
                          </AppText>
                          <YStack flex={1} gap={4}>
                            <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
                              {pack.title}
                            </AppText>
                            <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
                              {pack.description}
                            </AppText>
                          </YStack>
                        </XStack>
                        <XStack flexWrap="wrap" gap="$xs">
                          <AppButton size="sm" onPress={() => navigate(`/support/pack/${pack.slug}`)}>
                            Open helper pack
                          </AppButton>
                          <AppButton tone="outline" size="sm" onPress={() => void handleToggleSavedPack(pack.slug)}>
                            Remove saved
                          </AppButton>
                        </XStack>
                      </AppCardBody>
                    </AppCard>
                  ))}
                </YStack>
              </YStack>
            ) : null}

            <YStack position="relative">
              <XStack position="absolute" left={12} top={13} zIndex={1}>
                <Search size={18} className="text-muted-foreground" />
              </XStack>
              <AppInput
                type="text"
                value={search}
                onChangeText={setSearch}
                placeholder="Search support, packs, or topics..."
                paddingLeft={40}
              />
            </YStack>
          </YStack>

          <YStack gap="$sm">
            <AppSectionHeader
              title="More practical guides"
              subtitle="When you want the fuller checklist, these packs go step by step."
            />

            <XStack gap="$sm" overflowX="auto" paddingBottom="$xs">
              {visibleHelperPacks.length ? (
                visibleHelperPacks.map((pack) => (
                  <TamaguiButton
                    key={pack.slug}
                    unstyled
                    type="button"
                    onPress={() => navigate(`/support/pack/${pack.slug}`)}
                    minWidth={216}
                    width={216}
                    alignItems="stretch"
                    justifyContent="stretch"
                    pressStyle={{ opacity: 0.94 }}
                  >
                    <AppCard backgroundColor="$primary" borderColor="$primary" minHeight={104}>
                      <AppCardBody gap="$sm" flex={1}>
                        <XStack alignItems="flex-start" gap="$sm">
                          <AppText fontSize={26} lineHeight={28}>
                            {pack.emoji}
                          </AppText>
                          <AppText
                            flex={1}
                            fontFamily={kindredFontFamilies.sansBold}
                            fontSize="$sm"
                            lineHeight="$sm"
                            color="$primaryForeground"
                          >
                            {pack.title}
                          </AppText>
                        </XStack>
                        <YStack gap={4}>
                          <AppText color="rgba(251, 248, 242, 0.86)" fontSize="$sm" lineHeight="$md">
                            {pack.description}
                          </AppText>
                        </YStack>
                      </AppCardBody>
                    </AppCard>
                  </TamaguiButton>
                ))
              ) : hasActiveSearch ? (
                <AppEmptyState
                  title="No helper packs matched"
                  body="Try a broader term, or use the guided routes above to start somewhere useful."
                />
              ) : null}
            </XStack>
          </YStack>

          <YStack gap="$sm">
            <AppSectionHeader
              title="Browse by topic"
              subtitle="If you already know the area, head straight there."
            />

            <div className="grid grid-cols-2 gap-3">
              {filteredCategories.length ? (
                filteredCategories.map((category) => (
                  <TamaguiButton
                    key={category.slug}
                    unstyled
                    type="button"
                    onPress={() => navigate(`/support/${category.slug}`)}
                    width="100%"
                    alignItems="stretch"
                    justifyContent="stretch"
                    pressStyle={{ opacity: 0.94 }}
                  >
                    <AppCard height="100%">
                      <AppCardBody alignItems="center">
                        <AppText fontSize="$hero" lineHeight="$hero">
                          {category.emoji}
                        </AppText>
                        <AppText
                          textAlign="center"
                          fontFamily={kindredFontFamilies.sansBold}
                          fontSize="$sm"
                          lineHeight="$sm"
                        >
                          {category.name}
                        </AppText>
                        <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm" textAlign="center">
                          {category.description}
                        </AppText>
                      </AppCardBody>
                    </AppCard>
                  </TamaguiButton>
                ))
              ) : hasActiveSearch ? (
                <div className="col-span-2">
                  <AppEmptyState
                    title="No support areas matched"
                    body="Try a broader term, or start with one of the quicker routes above."
                  />
                </div>
              ) : null}
            </div>
          </YStack>
        </>
      ) : (
        <AppCard backgroundColor="$panelSoftBackground" borderColor="$borderStrong">
          <AppCardBody gap="$sm">
            <AppEmptyState
              title={
                supportPaused
                  ? `Support in ${supportCountryLabel} is paused`
                  : `Support for ${supportCountryLabel} is coming soon`
              }
              body={
                supportPaused
                  ? `${supportCountryLabel} is not on an active Kindred rollout right now. We have paused ${supportCountryLabel} while we put the right public, legal, and support layer in place. We will not show another country's rules as if they apply where you are.`
                  : `Kindred Community is ready in ${supportCountryLabel}, and local support guidance is still being built. Start with the local help below while we finish the fuller guidance. We will not show another country's rules as if they apply where you are.`
              }
            />
          </AppCardBody>
        </AppCard>
      )}

      {!liveSupportAvailable ? (
        <AppCard backgroundColor="$panelSoftBackground" borderColor="$borderStrong">
          <AppCardBody gap="$sm">
            <YStack gap={4}>
              <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
                Getting help in {supportCountryLabel}
              </AppText>
              <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
                {supportPaused
                  ? `${supportCountryLabel} is paused for now, so these are safer national starting points while Kindred holds back a wider launch until the local legal and support layer is properly ready.`
                  : "Full local Kindred guidance is still coming. These are safer local starting points than relying on UK-only support routes."}
              </AppText>
            </YStack>

            <XStack flexWrap="wrap" gap="$xs">
              {supportResourceBundle.starterLinks.map((link) => (
                <a key={link.href} href={link.href} style={{ textDecoration: "none" }}>
                  <XStack
                    borderWidth={1}
                    borderColor="$borderStrong"
                    borderRadius="$pill"
                    backgroundColor="$background"
                    paddingHorizontal={12}
                    paddingVertical={8}
                  >
                    <AppText color="$primary" fontSize="$sm" lineHeight="$sm" fontFamily={kindredFontFamilies.sansBold}>
                      {link.label}
                    </AppText>
                  </XStack>
                </a>
              ))}
            </XStack>
          </AppCardBody>
        </AppCard>
      ) : null}

      <TamaguiButton
        unstyled
        type="button"
        onPress={() => navigate("/editorial-policy")}
        alignSelf="flex-start"
        pressStyle={{ opacity: 0.85 }}
      >
        <XStack alignItems="center" gap={6}>
          <AppText color="$primary" fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
            How Kindred guidance works
          </AppText>
          <ArrowRight size={14} className="text-primary" />
        </XStack>
      </TamaguiButton>

      <AppCard backgroundColor="$secondarySoft" borderColor="$secondary">
        <AppCardBody gap="$sm">
          <YStack gap="$xs">
            <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
              Need help with Kindred itself?
            </AppText>
            <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
              For bugs, access problems, safeguarding, privacy, or formal complaints, start with the quickest route
              here. Use <strong>{KINDRED_FEEDBACK_CHANNEL_PATH}</strong> for ideas and product requests.
            </AppText>
          </YStack>

          <XStack flexWrap="wrap" gap="$xs">
            <TamaguiButton
              unstyled
              type="button"
              onPress={() => navigate("/communities")}
              pressStyle={{ opacity: 0.9 }}
            >
              <XStack
                borderWidth={1}
                borderColor="$secondary"
                borderRadius="$pill"
                backgroundColor="$background"
                paddingHorizontal={12}
                paddingVertical={8}
                alignItems="center"
                gap={6}
              >
                <AppText color="$secondary" fontSize="$sm" lineHeight="$sm" fontFamily={kindredFontFamilies.sansBold}>
                  Open Community
                </AppText>
                <ArrowRight size={13} className="text-secondary" />
              </XStack>
            </TamaguiButton>

            <TamaguiButton
              unstyled
              type="button"
              onPress={() => navigate("/contact-complaints")}
              pressStyle={{ opacity: 0.9 }}
            >
              <XStack
                borderWidth={1}
                borderColor="$secondary"
                borderRadius="$pill"
                backgroundColor="$background"
                paddingHorizontal={12}
                paddingVertical={8}
                alignItems="center"
                gap={6}
              >
                <AppText color="$secondary" fontSize="$sm" lineHeight="$sm" fontFamily={kindredFontFamilies.sansBold}>
                  Contact &amp; complaints
                </AppText>
                <ArrowRight size={13} className="text-secondary" />
              </XStack>
            </TamaguiButton>
          </XStack>
        </AppCardBody>
      </AppCard>

      <AppCard backgroundColor="$accentSoft" borderColor="$accent">
        <AppCardBody gap="$sm">
          <YStack gap={4}>
            <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
              Need support right now?
            </AppText>
            <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
              If today feels unsafe, overwhelming, or too much to hold, start here first.
            </AppText>
          </YStack>

          <XStack flexWrap="wrap" gap="$xs">
            {supportResourceBundle.urgentLinks.map((link) => (
              <a key={link.href} href={link.href} style={{ textDecoration: "none" }}>
                <XStack
                  borderWidth={1}
                  borderColor="$accent"
                  borderRadius="$pill"
                  backgroundColor="$background"
                  paddingHorizontal={12}
                  paddingVertical={8}
                >
                  <AppText color="$secondary" fontSize="$sm" lineHeight="$sm" fontFamily={kindredFontFamilies.sansBold}>
                    {link.label}
                  </AppText>
                </XStack>
              </a>
            ))}
          </XStack>
        </AppCardBody>
      </AppCard>
    </YStack>
  );
};

export default Support;
